import * as Sentry from '@sentry/react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '@utilitywarehouse/hearth-react';
import { difference } from 'lodash';
import { useEffect } from 'react';

import { useSyncState } from '@hooks/useSyncState';

import { getIdsFromSlug, getQuestionSlug } from './controlledAccordionState';
import { AccordionChangeHandler, FAQTopic } from './types';

interface SectionProps<T extends string> {
  title: string;
  topics: FAQTopic<T>[];
  onAccordionChange?: AccordionChangeHandler;
  expandedState?: string[];
  sectionId: string;
}

const getQuestionFromSlug = (slug: string, topics: FAQTopic<string>[]) => {
  const { questionId } = getIdsFromSlug(slug);
  return topics.find((topic) => topic.id === questionId)?.question ?? null;
};

const Section = <T extends string>({
  title,
  topics,
  onAccordionChange,
  expandedState,
  sectionId,
}: SectionProps<T>) => {
  const [state, setState] = useSyncState<string[]>(sectionId, []);

  const handleChangeCallback = (slug: string, isExpanded: boolean) => {
    const question = getQuestionFromSlug(slug, topics);

    if (!question) {
      return Sentry.captureMessage(
        'FAQ data inconsistency: missing question details',
        {
          level: 'error',
        }
      );
    }

    if (onAccordionChange) {
      onAccordionChange(question, isExpanded, slug);
    }
  };

  const setAccordionState = (value: string[], expandedStateUpdate: boolean) => {
    // expandedState is the external state prop for managing topic expansion from outside the component.
    // expandedStateUpdate determines whether to compare the existing state against expandedState - preventing unnecessary re-renders
    // This logic does not differentiate collapsing topics.
    // As of writing, only expansion is managed at the higher level.
    if (expandedStateUpdate) {
      if (difference(value, state).length === 0) {
        return;
      }

      return setState(value);
    }

    setState(value);
  };

  const handleAccordionChange = (
    value: string[],
    expandedStateUpdate = false
  ) => {
    setAccordionState(value, expandedStateUpdate);
    const expandedSlug = difference(value, state)[0];

    if (expandedSlug) {
      handleChangeCallback(expandedSlug, true);
      return;
    }

    const collapsedSlug = difference(state, value)[0];

    if (collapsedSlug) {
      handleChangeCallback(collapsedSlug, false);
      return;
    }
  };

  useEffect(() => {
    if (expandedState && expandedState.length > 0) {
      handleAccordionChange(expandedState, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedState]);

  return (
    <div className="faq-section__wrapper">
      <Accordion
        onValueChange={handleAccordionChange}
        type="multiple"
        heading={title}
        value={state}
      >
        {topics.map(({ id, question, answer }) => (
          <AccordionItem
            key={id}
            title={question}
            value={getQuestionSlug(sectionId, id)}
            id={id}
          >
            <AccordionContent>{answer}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

export { Section };
